import { useState } from 'react';
import type { AuditLogDTO } from '@pickem/shared';
import { useApi } from '../../lib/useApi';
import { Spinner, ErrorBanner, Card, SectionTitle } from '../../components/ui';
import { formatDateTime } from '../../lib/format';

export function AdminAuditPage() {
  const { data, error, loading } = useApi<{ logs: AuditLogDTO[] }>('/admin/audit?limit=200');
  const [expanded, setExpanded] = useState<string | null>(null);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="space-y-4">
      <SectionTitle>Audit Log</SectionTitle>
      <Card className="p-0">
        <ul className="divide-y divide-line">
          {data?.logs.map((l) => (
            <li key={l.id} className="px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="chip bg-grove/10 text-grove">{prettyAction(l.action)}</span>
                  <span className="ml-2 text-sm">{l.summary}</span>
                </div>
                <span className="text-xs text-muted">{formatDateTime(l.createdAt)}</span>
              </div>
              <div className="mt-1 text-xs text-muted">
                by <span className="font-medium text-body">{l.staffName}</span>
                {l.affectedPlayerName && <> · affected {l.affectedPlayerName}</>}
                {l.weekNumber != null && <> · Week {l.weekNumber}</>}
                {(l.before != null || l.after != null) && (
                  <button
                    className="ml-2 text-grove underline"
                    onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                  >
                    {expanded === l.id ? 'hide details' : 'details'}
                  </button>
                )}
              </div>
              {expanded === l.id && (
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <BeforeAfter label="Before" value={l.before} />
                  <BeforeAfter label="After" value={l.after} />
                </div>
              )}
            </li>
          ))}
          {data?.logs.length === 0 && (
            <li className="px-4 py-8 text-center text-muted">No audit entries yet.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}

function prettyAction(action: string): string {
  return action
    .toLowerCase()
    .split('_')
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(' ');
}

function BeforeAfter({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null;
  return (
    <div className="rounded border border-line bg-cream/50 p-2">
      <div className="mb-1 text-xs font-semibold uppercase text-muted">{label}</div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs text-body">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
